import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getProfile, updateProfile, changePassword, uploadImage, getMyCertificates } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Lock, Loader2, Save, CheckCircle, X, Camera, Linkedin, Github, Globe, Award } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated) {
      const fetchProfile = async () => {
        try {
          const [profileData, certsData] = await Promise.all([
            getProfile(),
            getMyCertificates()
          ]);
          setProfile(profileData);
          setCertificates(certsData);
        } catch (error) {
          console.error('Failed to fetch profile:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchProfile();
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleProfileChange = (field, value) => {
    setProfile({ ...profile, [field]: value });
  };

  const handleSocialLinkChange = (network, value) => {
    setProfile({
      ...profile,
      social_links: { ...profile.social_links, [network]: value }
    });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const response = await uploadImage(file);
      // Assuming response contains the URL or we construct it
      // The API returns { url: "..." }
      const avatarUrl = response.url;

      // Update profile with new avatar URL immediately
      // We might need to call updateProfile if the backend doesn't auto-update user on upload
      // But usually uploadImage just returns URL. Let's update the profile.
      // Wait, User model doesn't have avatar_url field explicitly shown in my previous view?
      // Let's assume there is one or I use a generic field.
      // Actually previous User model view didn't show avatar_url. I might need to add it or it's dynamic.
      // Let's check User model again?
      // Step 1375 showed: first_name, last_name, bio, skills. No avatar.
      // I should add `avatar_url` to User model too!
      // For now I'll continue editing ProfilePage and I'll update User model in next step if needed.
      // I'll proceed assuming `avatar_url` exists or I'll add it.

      setProfile({ ...profile, avatar_url: avatarUrl });
      await updateProfile({ ...profile, avatar_url: avatarUrl });
      toast.success('Avatar updated successfully');
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAddSkill = (e) => {
    if (e.key === 'Enter' && skillInput.trim()) {
      e.preventDefault();
      const newSkills = [...(profile.skills || []), skillInput.trim()];
      setProfile({ ...profile, skills: newSkills });
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    const newSkills = profile.skills.filter((skill) => skill !== skillToRemove);
    setProfile({ ...profile, skills: newSkills });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateProfile({
        first_name: profile.first_name,
        last_name: profile.last_name,
        title: profile.title,
        bio: profile.bio,
        skills: profile.skills,
        social_links: profile.social_links,
        avatar_url: profile.avatar_url
      });
      updateUser({
        first_name: profile.first_name,
        last_name: profile.last_name
      });
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getInitials = () => {
    if (!profile) return 'U';
    return `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase() || 'U';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-6 mb-10">
          <Avatar className="w-20 h-20 border-4 border-primary/20">
            <AvatarFallback className="bg-primary text-white text-2xl font-medium">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                getInitials()
              )}
            </AvatarFallback>
            <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-full group">
              {uploadingAvatar ? (
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-white" />
              )}
              <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} disabled={uploadingAvatar} />
            </label>
          </Avatar>
          <div>
            <h1 className="font-heading font-bold text-3xl" data-testid="profile-name">
              {profile?.first_name} {profile?.last_name}
            </h1>
            <p className="text-muted-foreground">{profile?.title || 'Learner'}</p>
            <p className="text-sm text-muted-foreground">{profile?.email}</p>
            {profile?.role === 'admin' && (
              <Badge className="mt-2 bg-primary/10 text-primary">Admin</Badge>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-8">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="profile" className="gap-2" data-testid="tab-profile">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="certificates" className="gap-2" data-testid="tab-certificates">
              <Award className="w-4 h-4" />
              Certificates
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2" data-testid="tab-security">
              <Lock className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      value={profile?.first_name || ''}
                      onChange={(e) => handleProfileChange('first_name', e.target.value)}
                      className="h-11"
                      data-testid="profile-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      value={profile?.last_name || ''}
                      onChange={(e) => handleProfileChange('last_name', e.target.value)}
                      className="h-11"
                      data-testid="profile-lastname"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Title / Headline</Label>
                  <Input
                    id="title"
                    placeholder="e.g. Software Engineer"
                    value={profile?.title || ''}
                    onChange={(e) => handleProfileChange('title', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell us about yourself..."
                    value={profile?.bio || ''}
                    onChange={(e) => handleProfileChange('bio', e.target.value)}
                    rows={4}
                    data-testid="profile-bio"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Skills</Label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {profile?.skills?.map((skill, index) => (
                      <Badge key={index} variant="secondary" className="gap-1 py-1">
                        {skill}
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-destructive"
                          onClick={() => handleRemoveSkill(skill)}
                        />
                      </Badge>
                    ))}
                  </div>
                  <Input
                    placeholder="Type a skill and press Enter"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleAddSkill}
                    className="h-11"
                    data-testid="profile-skill-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Press Enter to add a skill
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Social Links</Label>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <Linkedin className="w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="LinkedIn URL"
                        value={profile?.social_links?.linkedin || ''}
                        onChange={(e) => handleSocialLinkChange('linkedin', e.target.value)}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Github className="w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="GitHub URL"
                        value={profile?.social_links?.github || ''}
                        onChange={(e) => handleSocialLinkChange('github', e.target.value)}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Website URL"
                        value={profile?.social_links?.website || ''}
                        onChange={(e) => handleSocialLinkChange('website', e.target.value)}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="rounded-full"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates">
            <Card>
              <CardHeader>
                <CardTitle>My Certificates</CardTitle>
                <CardDescription>View and download your earned certificates</CardDescription>
              </CardHeader>
              <CardContent>
                {certificates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Award className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>You haven't earned any certificates yet.</p>
                    <p className="text-sm">Complete courses to earn certificates!</p>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {certificates.map((cert) => (
                      <div key={cert.id} className="border rounded-lg p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div>
                          <h3 className="font-semibold">{cert.course_title}</h3>
                          <p className="text-sm text-muted-foreground">Issued: {new Date(cert.issued_at).toLocaleDateString()}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${cert.id}/download`, '_blank')}>
                          Download PDF
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your password to keep your account secure</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                      className="h-11 max-w-md"
                      data-testid="current-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      className="h-11 max-w-md"
                      data-testid="new-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm new password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      className="h-11 max-w-md"
                      data-testid="confirm-password"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="rounded-full"
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 w-4 h-4" />
                        Update Password
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
